<template>
  <NuxtLayout name="loyalty">
    <section class="points__Header container--Medium defaultSectionMargins">
      <h1 v-text="page.title" />

      <client-only>
        <div v-if="userData?.loyalty" class="points__StatusWrapper">
          <div class="points__StatusInfosContainer">
            <div v-if="pointsResponse?.expiration_date" class="points__StatusInfos">
              <p v-text="$t('pointsPage.expirationDate')" />
              <span>{{ format(new Date(pointsResponse.expiration_date), 'dd.MM.yyyy.') }}</span>
            </div>
            <div class="points__StatusInfos">
              <p v-text="$t('pointsPage.cardNumber')"></p>
              <span>{{ userData.loyalty.card_number }}</span>
            </div>
          </div>
  
          <div class="points__StatusProgress">
            <div class="points__CurrentNumber">
              <template v-if="currentLevel && nextLevel">
                {{ userPoints }}
              </template>
              <template v-else-if="currentLevel && !nextLevel">
                {{ currentLevel.points_from }} +
              </template>
            </div>
            <div class="points__ProgressWrapper">
              <template v-if="currentLevel && nextLevel">
                {{ parseInt(currentLevel.points_from) }}
              </template>
              <template v-else-if="!nextLevel">0</template>
              <div class="points__Progress">
                <div v-if="nextLevel" class="points__ProgressFill" :style="`transform: translateX(${ levelProgress })`"></div>
                <div v-else class="points__ProgressFill lastLevel" :style="`transform: translateX(100%)`"></div>
              </div>
              <div class="points__ProgressNext">
                <template v-if="nextLevel?.points_from">
                  {{ parseInt(nextLevel.points_from) }}
                </template>
                <template v-else-if="currentLevel">
                  {{ parseInt(currentLevel.points_from) }}
                </template>
              </div>
            </div>
            <div class="points__ProgressTitles">
              <template v-if="currentLevel && nextLevel">
                <span>{{ currentLevel.name }}</span>
                <span>{{ nextLevel.name }}</span>
              </template>
              <template v-else-if="previousLevel">
                <span>{{ previousLevel.name }}</span>
                <span v-if="currentLevel"><Icon name="padlock" />{{ currentLevel.name }}</span>
              </template>
            </div>
          </div>
        </div>
      </client-only>
    </section>

    <section v-if="nextLevel" class="points__PremiumRibbon">
      <div class="container--Medium">
        <Icon name="padlock-unlocked" />
        <i18n-t tag="p" scope="global" keypath="pointsPage.premiumInfo">
          <template #points>
            {{ $t('pointsPlural', remainingPoints) }}
          </template>
          <template #level>
            <button v-if="nextLevel?.features?.length" type="button" @click="open('premium')">{{ nextLevel.name }} {{ $t('pointsPage.level') }}</button>
            <span v-else>{{ nextLevel.name }} {{ $t('pointsPage.level') }}</span>
          </template>
        </i18n-t>
      </div>
    </section>

    <section v-if="currentLevel?.features" class="points__UspWrapper defaultSectionPaddings">
      <div class="container--Medium">
        <h2 v-text="$t('pointsPage.levelBenefits')" />
        <div class="points__UspItems" :class="{'hasLargeGrid': currentLevel.features.length > 3}">
          <div v-for="item in currentLevel.features" class="points__UspItem">
            <Flare :srcset="item.image" size="both" width="100" height="100" crop="fill" :alt="item.image" />
            {{ item.short_description || item.description }}
          </div>
        </div>
      </div>
    </section>

    <section class="points__ActivityListing container--Medium defaultSectionMargins">
      <h2 v-text="$t('pointsPage.activityTitle')" />

      <div v-if="results?.collection?.length" class="points__Activity">
        <div class="points__ActivityTitles">
          <div v-text="$t('pointsPage.date')" />
          <div>
            <div class="points__Points" v-text="$t('pointsPage.points')" />
          </div>
        </div>
        
        <div v-for="item in results.collection" class="points__ActivityItem">
          <div class="points__DateAndLocation">
            <div class="points__Date">{{ format(new Date(item.created_on), 'dd.MM.yyyy.') }}</div>
            <div class="points__Location" v-text="item.transaction_name" />
          </div>

          <div :class="{'isPending': item.status === 'pending'}">
            <div class="points__StatusAndPoints">
              <div class="points__Status"><template v-if="item.status === 'pending'">{{ item.status }}</template></div>
              <div class="points__Points"><template v-if="[100000001, 100000002].includes(item.action_code)">-</template><template v-else>+</template> {{ item.points_change }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="points__NoActivity">
        <p>{{ $t('pointsPage.noActivity') }}</p>
      </div>

      <LoyaltyPagination
        :data="results"
        :current-page="currentPage"
        @previous-results="fetchResults('previous')"
        @next-results="fetchResults('next')"
        @go-to-page="changePage"
      />
    </section>

    <Modal v-show="dialogs.premium && nextLevel" v-model="dialogs.premium" class="points__Modal">
      <div class="points__ModalHeader">
        <i18n-t tag="h3" scope="global" keypath="pointsPage.benefits">
          <template #loyaltyLevel>
            <template v-if="nextLevel?.name">{{ nextLevel.name }}</template>
          </template>
        </i18n-t>

        <p v-if="nextLevel?.points_from">{{ $t('pointsPage.moreThenPoints', nextLevel.points_from) }}</p>
      </div>

      <div class="points__ModalContent">
        <div v-if="nextLevel?.features" class="points__ModalUsps">
          <div v-for="item in nextLevel.features" class="points__ModalUspItem">
            <div>
              <Flare :srcset="item.image" size="both" width="100" height="100" crop="fill" :alt="item.image" />
            </div>
            {{ item.description }}
          </div>
        </div>
      </div>
    </Modal>
  </NuxtLayout>
</template>

<script setup>
  import { format, isPast } from 'date-fns';
  
  defineOptions({
    auth: 'aminess',     
  })

  const props = defineProps({
    page: {}
  })
  const { user: userData } = useAuth('aminess');
  const { results, currentPage, fetchResults } = await useLoyaltyPagination('transactions');
  const pointsResponse = ref(null)

  const userPoints = computed(() => userData?.value?.loyalty?.cumulative_points || 0);
  const loyalyLevels = computed(() => props.page.loyalty_levels);
  const levelsOrdered = computed(() => loyalyLevels.value.sort((a, b) => a.points_to - b.points_to));
  const currentLevelIndex = computed(() => levelsOrdered.value.findIndex(level => level.points_to >= userPoints.value));
  const previousLevel = computed(() => currentLevelIndex.value !== -1 ? levelsOrdered.value[currentLevelIndex.value - 1] : null);
  const currentLevel = computed(() => levelsOrdered.value[currentLevelIndex.value]);
  const nextLevel = computed(() => currentLevelIndex.value !== -1 ? levelsOrdered.value[currentLevelIndex.value + 1] : null);
  const levelProgress = computed(() => ((userPoints.value - currentLevel.value.points_from) / (nextLevel.value.points_from - currentLevel.value.points_from) * 100).toFixed(2) + '%');
  const remainingPoints = computed(() => nextLevel.value.points_from - userPoints.value);

  const dialogs = reactive({
    premium: false
  });
  const { open } = useModal(dialogs);

  try{
    pointsResponse.value = await useAuthFetch('points', { strategy: 'aminess' });
  } catch(error){
    console.dir(error)
  }

  function changePage(value) {
    fetchResults(null, value);
  }

</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_points.scss');
</style>